import React, { Component } from "react";
import { Helmet } from "react-helmet";
import { observer, inject } from "mobx-react";
import {
  FormGroup,
  ControlGroup,
  TextArea,
  Button,
  Alert,
  Intent
} from "@blueprintjs/core";
import ClientAutocomplete from "components/ClientAutocomplete";
import LocationAutocomplete from "components/LocationAutocomplete";
import ServiceTypePicker from "components/ServiceTypePicker";
import AddProjectModal from "components/Project/AddProjectModal";
import AddLocationModal from "components/Client/AddLocationModal";
import AddClientModal from "components/Client/AddClientModal";
import { FormSection, Choice, FieldError, ClientFlags } from "components/FormBits";
import { observable } from "mobx";
import moment from "moment-timezone";
import { TimePicker } from "@blueprintjs/datetime";
import RRuleGenerator from "components/react-rrule-generator";
import RepeatPicker, { VisitPreview } from "components/ServiceAgreement/RepeatPicker";
import { VisitTimes } from "components/Job/WhenPicker";
import { newJobTimes } from "helpers/jobTimes";
import { Api } from "services/Api";
import Toast from "helpers/toaster";
import FormDrawer from "components/FormDrawer";
import "../../../node_modules/react-rrule-generator/build/styles.css";
// import RecurringJob from "components/ServiceAgreement/RecurringJob"

// Two ways to set the schedule: the full repeat options with visit start and
// end times (as before), or the simpler repeat picker. Both write the same
// rrule, start_time and end_time. The form always opens on All options.
const SCHEDULE_MODES = [
  { value: "full", label: "All options" },
  { value: "simple", label: "Simple" },
];

const STATUSES = [
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
];

// Field wrappers, in the order they appear: the first with a problem gets focus.
const FIELD_IDS = {
  client: "sa-field-client",
  service_type: "sa-field-service-type",
  title: "sa-field-title",
  reason: "sa-field-reason",
  repeat: "sa-field-repeat",
  status: "sa-field-status",
};

// New/Edit service agreement, in a side panel. Mounted afresh each time it
// opens (pages/jobs/new.js); `isOpen` and `onClose` come from there.
@inject("store", "projectStore", "serviceAgreementStore", "jobStore")
@observer
class PersistServiceAgreement extends Component {
  @observable isOpenAddClientDialog = false;
  @observable isOpenAddProjectDialog = false;
  @observable isOpenAddLocationDialog = false;
  @observable isUpdateDialogVisible = false;
  @observable scheduleMode = "full";
  @observable minDateStart = moment().toDate();
  @observable startDateDefault = moment().toDate();
  @observable minDateEnd = moment().add(1, 'hour').toDate();
  @observable endDateDefault = moment().add(1, 'hour').toDate();
  // Set on the first Save; from then on problems show (and clear) as you type.
  @observable submitted = false;

  componentDidMount() {
    let { object } = this.props.serviceAgreementStore.persistServiceAgreement;
    if (object && !object.id) {
      object.start_date = this.startDateDefault;
      object.end_date = this.endDateDefault;
      // Visits start on the 15-minute grid, at least 15 minutes from now.
      const { start, end } = newJobTimes(moment(), 15);
      object.start_time = start.toDate();
      object.end_time = end.toDate();
    }
    // Opened for a client (client page): use its location if it has only one.
    if (object && object.client_id && !this.hasLocation(object)) {
      this.useOnlyLocation(object.client_id);
    }
  }

  hasLocation(object) {
    return Boolean(
      (object.location_ids && object.location_ids.length > 0) ||
      object.location ||
      (object.locations && object.locations.length > 0)
    );
  }

  // If the client has exactly one location, pick it (never replaces a choice).
  useOnlyLocation(clientId) {
    const search = { term: '', per_page: 2, page: 1, order: { name: 'asc' }, where: { client_id: clientId } };
    Api.post('/locations/search', { search })
      .then((response) => {
        const { object } = this.props.serviceAgreementStore.persistServiceAgreement;
        const locations = (response.data && response.data.data) || [];
        if (locations.length === 1 && object && object.client_id === clientId && !this.hasLocation(object)) {
          object.location_ids = [locations[0].id];
          object.location = locations[0];
        }
      })
      .catch(() => {});
  }

  onServiceTypeSelect(selectedOptions) {
    let { object } = this.props.serviceAgreementStore.persistServiceAgreement;
    object.selectedServiceType = selectedOptions;
    // Unticking the service type clears it, so the required check sees it.
    object.service_type_id = selectedOptions.length > 0 ? selectedOptions[0].value : null;
  }

  handleCloseProjectDailog() {
    this.isOpenAddProjectDialog = false;
  }

  handleCloseClientDailog() {
    this.isOpenAddClientDialog = false;
  }

  handleCloseLocationDailog() {
    this.isOpenAddLocationDialog = false;
  }

  onLocationClear = () => {
    let { object } = this.props.serviceAgreementStore.persistServiceAgreement;
    object.location_ids = [];
    object.locations = [];
    object.location = null;
  };

  onClientClear = () => {
    let { object } = this.props.serviceAgreementStore.persistServiceAgreement;
    object.client_id = null;
    object.client = null;
    this.onLocationClear();
  };

  onSuccessAddClient(client) {
    let { object } = this.props.serviceAgreementStore.persistServiceAgreement;
    this.isOpenAddClientDialog = false;
    object.client_id = client.id;
    object.client = client;
  }

  onSuccessAddLocation(location) {
    let { object } = this.props.serviceAgreementStore.persistServiceAgreement;
    this.isOpenAddLocationDialog = false;
    object.location_id = location.id;
    // The agreement's locations are `location_ids` (as picking one sets).
    object.location_ids = [location.id];
    object.location = location;
  }

  onSuccessAddProject() {
    this.isOpenAddProjectDialog = false;
  }


  onStartDateChange = time => {
    let { object } = this.props.serviceAgreementStore.persistServiceAgreement;
    object.start_date = time;
    this.startDateDefault = time;

    if(object.start_date > this.minDateEnd){
      object.end_date = moment(time).add(1, 'hour').toDate();
      this.minDateEnd = moment(time).add(1, 'hour').toDate();
      this.endDateDefault = moment(time).add(1, 'hour').toDate();
    }else{
      object.end_date = moment(time).add(1, 'hour').toDate();
      this.minDateEnd = moment(time).add(1, 'hour').toDate();
    }
  };

  onEndDateChange = time => {
    let { object } = this.props.serviceAgreementStore.persistServiceAgreement;
    object.end_date = moment(time).toDate();
    this.endDateDefault = moment(time).toDate();
  };

  // What's missing, by field.
  validate() {
    const { object } = this.props.serviceAgreementStore.persistServiceAgreement;
    const errors = {};
    if (!object.client_id && !object.client) {
      errors.client = 'Choose the customer for this agreement.';
    }
    if (!object.service_type_id) {
      errors.service_type = 'Choose a service type.';
    }
    if (object.title === '' || object.title === null) {
      errors.title = 'Give the agreement a title.';
    }
    if (object.reason_for_call === '' || object.reason_for_call === null) {
      errors.reason = 'Say what these visits are for.';
    }
    if (!object.rrule) {
      errors.repeat = 'Choose how often the visits repeat.';
    }
    if (object.status === '' || object.status === null || !object.status) {
      errors.status = 'Choose Draft or Active.';
    }
    return errors;
  }

  // On Save: true when fine; otherwise shows the problems and goes to the first.
  checkForm() {
    this.submitted = true;
    const errors = this.validate();
    const first = Object.keys(FIELD_IDS).find((key) => errors[key]);
    if (!first) return true;
    setTimeout(() => {
      const wrapper = document.getElementById(FIELD_IDS[first]);
      if (!wrapper) return;
      wrapper.scrollIntoView({ block: 'center', behavior: 'smooth' });
      const target = wrapper.querySelector('input:not([type=hidden]), textarea, button');
      if (target) target.focus({ preventScroll: true });
    }, 60);
    return false;
  }

  createServiceAgreement() {
    if (!this.checkForm()) return;
    this.props.serviceAgreementStore
      .create()
      .then(this.onSuccessfullCreateServiceAgreement.bind(this))
      .catch(this.onFailCreateServiceAgreement.bind(this));
  }

  onSuccessfullCreateServiceAgreement(response) {
    this.props.store.isOpen = false;
    this.props.jobStore.isServiceAgreement = false;
    Toast.show({
      icon: "thumbs-up",
      message: "Service Agreement created/updated successfully!",
      intent: Intent.SUCCESS
    });
  }

  onFailCreateServiceAgreement(error) {
    if (error.response && error.response.data && error.response.data.errors) {
      Toast.show({
        icon: "warning-sign",
        message: error.response.data.errors,
        intent: Intent.DANGER
      });
    } else {
      Toast.show({
        icon: "warning-sign",
        message:
          "Failed to create/update service agreement. Something went wrong!",
        intent: Intent.DANGER
      });
    }
  }

  handleUpdateConfirm() {
    this.isUpdateDialogVisible = false;
    if (!this.checkForm()) return;
    this.props.serviceAgreementStore
      .update()
      .then(this.onSuccessfullCreateServiceAgreement.bind(this))
      .catch(this.onFailCreateServiceAgreement.bind(this));
  }

  handleUpdateCancel() {
    this.isUpdateDialogVisible = false;
  }

  updateServiceAgreement() {
    // Check the fields first, so nobody confirms an update that can't be saved.
    if (!this.checkForm()) return;
    this.isUpdateDialogVisible = true;
  }

  render() {
    let {
      object,
      isLoading
    } = this.props.serviceAgreementStore.persistServiceAgreement;
    let { isOpen, onClose } = this.props;
    // While the panel slides out the agreement has already been cleared; keep
    // the title it opened with.
    if (isOpen) {
      this.isEdit = !!(object && object.id);
    }
    const isEdit = this.isEdit;
    const title = isEdit ? "Edit service agreement" : "New service agreement";
    const errors = this.submitted ? this.validate() : {};
    const locations = object.locations || [];
    // A location picked here ({ id, attributes }). When editing, `location`
    // holds the saved agreement's raw address instead, and `locations` is shown.
    const pickedLocation = object.location && object.location.attributes ? object.location : null;

    return (
      <React.Fragment>
        {isOpen && (
          <Helmet>
            <title>{title}</title>
          </Helmet>
        )}
        <FormDrawer
          className="job-form"
          isOpen={isOpen}
          title={title}
          icon={isEdit ? "edit" : "document-open"}
          size="min(760px, 100vw)"
          onClose={onClose}
          busy={!!isLoading}
          footer={
            <>
              <Button text="Cancel" onClick={onClose} disabled={!!isLoading} />
              {isEdit ? (
                <Button
                  icon="floppy-disk"
                  text="Save changes"
                  intent={Intent.PRIMARY}
                  onClick={this.updateServiceAgreement.bind(this)}
                  loading={isLoading}
                />
              ) : (
                <Button
                  icon="add"
                  text="Create service agreement"
                  intent={Intent.PRIMARY}
                  onClick={this.createServiceAgreement.bind(this)}
                  loading={isLoading}
                />
              )}
            </>
          }
        >
          <FormSection title="Customer" hint="Who the agreement is with and where the visits happen.">
            <FormGroup
              label={<span className="fb-label">Customer<span className="fb-required" aria-hidden="true"> *</span></span>}
              labelFor="sa-client"
              className={errors.client ? 'has-error' : ''}
            >
              <div id={FIELD_IDS.client}>
                <ControlGroup fill={true} vertical={false} className="fb-picker">
                  <ClientAutocomplete
                    showDetails={true}
                    inputProps={{
                      id: "sa-client",
                      placeholder: "Search by name, phone number or email",
                      'aria-required': true,
                      'aria-invalid': Boolean(errors.client),
                    }}
                    value={object.client}
                    onClientSelect={client => {
                      if (object && client) {
                        object.client_id = client.id;
                        object.client = client;
                        if (!this.hasLocation(object)) {
                          this.useOnlyLocation(client.id);
                        }
                      } else {
                        this.onClientClear();
                      }
                    }}
                  />
                  <Button
                    icon="add"
                    text="New client"
                    onClick={() => {
                      this.isOpenAddClientDialog = true;
                    }}
                    className="fb-add-button"
                  />
                </ControlGroup>
              </div>
              <FieldError>{errors.client}</FieldError>
              <ClientFlags client={object.client} />
            </FormGroup>
            {object.client_id && (
              <FormGroup label={<span className="fb-label">Location</span>} labelFor="sa-location">
                <ControlGroup fill={true} vertical={false} className="fb-picker">
                  <LocationAutocomplete
                    scope={{ client_id: object.client_id }}
                    inputProps={{
                      id: "sa-location",
                      fill: true,
                      placeholder: "Search this client's locations"
                    }}
                    onLocationClear={this.onLocationClear.bind(this)}
                    value={pickedLocation || locations[0]}
                    onLocationSelect={location => {
                      if (object && location) {
                        object.location_ids = [location.id];
                        object.location = location;
                      } else {
                        this.onLocationClear();
                      }
                    }}
                  />
                  <Button
                    icon="add"
                    text="New location"
                    onClick={() => {
                      this.isOpenAddLocationDialog = true;
                    }}
                    className="fb-add-button"
                  />
                </ControlGroup>
              </FormGroup>
            )}
          </FormSection>

          <FormSection title="The work" hint="What each visit is for. Every job made from this agreement gets these.">
            <div className="jf-grid">
              <div
                id={FIELD_IDS.service_type}
                className={`jf-field${errors.service_type ? ' has-error' : ''}`}
                role="group"
                aria-labelledby="sa-service-type-label"
              >
                <span className="fb-label" id="sa-service-type-label">
                  Service type<span className="fb-required" aria-hidden="true"> *</span>
                </span>
                <ServiceTypePicker
                  selectedOptions={object.selectedServiceType}
                  onChange={this.onServiceTypeSelect.bind(this)}
                  maximumSelections={1}
                  placeholder="Choose a service type"
                />
                <FieldError>{errors.service_type}</FieldError>
              </div>
              <FormGroup
                label={<span className="fb-label">Title<span className="fb-required" aria-hidden="true"> *</span></span>}
                labelFor="sa-title"
                className={errors.title ? 'has-error' : ''}
              >
                <div id={FIELD_IDS.title}>
                  <input
                    id="sa-title"
                    type="text"
                    className="bp3-input bp3-fill"
                    placeholder="e.g. Quarterly HVAC maintenance"
                    aria-required="true"
                    aria-invalid={Boolean(errors.title)}
                    defaultValue={object.title}
                    onChange={event => {
                      object.title = event.target.value;
                    }}
                  />
                </div>
                <FieldError>{errors.title}</FieldError>
              </FormGroup>
            </div>
            <FormGroup
              label={<span className="fb-label">Reason for call<span className="fb-required" aria-hidden="true"> *</span></span>}
              labelFor="sa-reason"
              className={errors.reason ? 'has-error' : ''}
            >
              <div id={FIELD_IDS.reason}>
                <TextArea
                  id="sa-reason"
                  rows={3}
                  className="bp3-fill"
                  placeholder="e.g. Inspect and clean rooftop units, replace filters."
                  aria-required="true"
                  aria-invalid={Boolean(errors.reason)}
                  value={object.reason_for_call || ''}
                  onChange={(event) => { object.reason_for_call = event.target.value } }
                />
              </div>
              <FieldError>{errors.reason}</FieldError>
            </FormGroup>
          </FormSection>

          <FormSection
            title="Schedule"
            hint="How often the visits repeat, and the time of day each one takes."
            aside={
              <div className="fb-switch" role="radiogroup" aria-label="Set the schedule with">
                {SCHEDULE_MODES.map(mode => (
                  <button
                    key={mode.value}
                    type="button"
                    role="radio"
                    aria-checked={this.scheduleMode === mode.value}
                    className={`fb-switch__option${this.scheduleMode === mode.value ? ' is-selected' : ''}`}
                    onClick={() => { this.scheduleMode = mode.value; }}
                  >
                    {mode.label}
                  </button>
                ))}
              </div>
            }
          >
            <div id={FIELD_IDS.repeat}>
              {this.scheduleMode === "simple" ? (
                <RepeatPicker
                  value={object.rrule}
                  onChange={(rrule) => {object.rrule = rrule}}
                  startTime={object.start_time}
                  endTime={object.end_time}
                  readOnly={Boolean(object.id)}
                >
                  <VisitTimes
                    start={object.start_time}
                    end={object.end_time}
                    onChange={({ start, end }) => { object.start_time = start; object.end_time = end; }}
                  />
                </RepeatPicker>
              ) : (
                <React.Fragment>
                  {!object.id && (
                    <FormGroup className="jf-rrule">
                      <RRuleGenerator
                        value={object.rrule}
                        onChange={(rrule) => {object.rrule = rrule}}
                        config={{
                          repeat: ['Yearly', 'Monthly', 'Weekly'],
                          end: ['Never', 'On date'],
                          hideStart: false,
                          hideEnd: false
                        }}
                      />
                    </FormGroup>
                  )}
                  <div className="jf-grid">
                    <div className="jf-field" role="group" aria-labelledby="sa-start-time-label">
                      <span className="fb-label" id="sa-start-time-label">
                        Visit starts at<span className="fb-required" aria-hidden="true"> *</span>
                      </span>
                      <TimePicker
                        useAmPm={false}
                        onChange={(date) => {object.start_time = moment(date).toDate()}}
                        value={object.start_time}
                      />
                    </div>
                    <div className="jf-field" role="group" aria-labelledby="sa-end-time-label">
                      <span className="fb-label" id="sa-end-time-label">
                        Visit ends at<span className="fb-required" aria-hidden="true"> *</span>
                      </span>
                      <TimePicker
                        useAmPm={false}
                        onChange={(date) => {object.end_time = moment(date).toDate()}}
                        value={object.end_time}
                      />
                    </div>
                  </div>
                  {object.rrule ? (
                    <VisitPreview rrule={object.rrule} startTime={object.start_time} endTime={object.end_time} />
                  ) : (
                    <p className="rp-note">Nothing is set yet: change an option above to choose how often the visits repeat.</p>
                  )}
                  {object.id && <p className="rp-note">The repeat is set when the agreement is created and can’t be changed here.</p>}
                </React.Fragment>
              )}
            </div>
            <FieldError>{errors.repeat}</FieldError>
          </FormSection>

          <FormSection title="Agreement" hint="Where the agreement stands, and what it costs.">
            <div className="jf-grid">
              <div id={FIELD_IDS.status}>
                <Choice
                  label="Status"
                  name="Status"
                  required={true}
                  value={object.status || ''}
                  onChange={(value) => { object.status = value; }}
                  options={STATUSES}
                  error={errors.status}
                />
              </div>
              <FormGroup label={<span className="fb-label">Price</span>} labelFor="sa-price">
                <input
                  id="sa-price"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.01"
                  className="bp3-input bp3-fill"
                  placeholder="e.g. 149.00"
                  defaultValue={object.price || ''}
                  onChange={event => {
                    object.price = event.target.value;
                  }}
                />
              </FormGroup>
            </div>
          </FormSection>
        </FormDrawer>
        {/* Outside the panel, so Escape in one of these closes only it. */}
        <AddProjectModal
          isOpen={this.isOpenAddProjectDialog}
          client_id={object.client_id}
          onClose={this.handleCloseProjectDailog.bind(this)}
          onSuccess={this.onSuccessAddProject.bind(this)}
        />
        <AddLocationModal
          isOpen={this.isOpenAddLocationDialog}
          client_id={object.client_id}
          onClose={this.handleCloseLocationDailog.bind(this)}
          onSuccess={this.onSuccessAddLocation.bind(this)}
        />
        <AddClientModal
          isOpen={this.isOpenAddClientDialog}
          onClose={this.handleCloseClientDailog.bind(this)}
          onSuccess={this.onSuccessAddClient.bind(this)}
        />
        <Alert
          cancelButtonText="Cancel"
          confirmButtonText="Update"
          icon="edit"
          intent={Intent.WARNING}
          isOpen={this.isUpdateDialogVisible}
          onCancel={this.handleUpdateCancel.bind(this)}
          onConfirm={this.handleUpdateConfirm.bind(this)}
        >
          <p>
            All associated future jobs will also be updated. Are you sure you want to do this?
          </p>
        </Alert>
      </React.Fragment>
    );
  }
}

export default PersistServiceAgreement;
